import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { addDays, parseISO, addYears, isWithinInterval, subDays } from 'date-fns';

interface DashboardData {
  upcomingBirthdays: Array<{
    id: string;
    first_name: string;
    last_name: string;
    birthday: string;
  }>;
  recentCommunications: Array<{
    id: string;
    first_name: string;
    last_name: string;
    last_contact: string;
    type: 'email' | 'manual';
  }>;
  needsFollowUp: Array<{
    id: string;
    first_name: string;
    last_name: string;
    work_email?: string;
    personal_email?: string;
  }>;
}

export function useDashboardData(daysRange: number = 30) {
  const [data, setData] = useState<DashboardData>({
    upcomingBirthdays: [],
    recentCommunications: [],
    needsFollowUp: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        const today = new Date();
        const futureDate = addDays(today, daysRange);
        const pastDate = subDays(today, daysRange);

        // Fetch all contacts with birthdays
        const { data: birthdayContacts, error: birthdayError } = await supabase
          .from('nrm_contacts')
          .select('id, first_name, last_name, birthday')
          .not('birthday', 'is', null)
          .order('birthday');

        if (birthdayError) throw birthdayError;

        // Filter upcoming birthdays
        const upcomingBirthdays = birthdayContacts?.filter(contact => {
          const birthday = parseISO(contact.birthday);
          let thisYearBirthday = new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate());
          
          if (thisYearBirthday < today) {
            thisYearBirthday = addYears(thisYearBirthday, 1);
          }

          return isWithinInterval(thisYearBirthday, { 
            start: today, 
            end: futureDate 
          });
        }).sort((a, b) => {
          const dateA = parseISO(a.birthday);
          const dateB = parseISO(b.birthday);
          return (dateA.getMonth() * 100 + dateA.getDate()) - 
                 (dateB.getMonth() * 100 + dateB.getDate());
        }) || [];

        // Fetch recent email messages within the time range
        const { data: emailMessages } = await supabase
          .from('nrm_email_messages')
          .select('from_email, date')
          .gte('date', pastDate.toISOString())
          .lte('date', today.toISOString())
          .order('date', { ascending: false });

        // Fetch timeline entries within the time range
        const { data: timelineEntries } = await supabase
          .from('nrm_timeline_entries')
          .select('contact_id, date')
          .gte('date', pastDate.toISOString())
          .lte('date', today.toISOString())
          .order('date', { ascending: false });

        // Fetch all contacts
        const { data: contacts } = await supabase
          .from('nrm_contacts')
          .select('id, first_name, last_name, work_email, personal_email');

        if (!contacts) throw new Error('Failed to fetch contacts');

        // Process recent communications
        const recentCommunications = [];
        const contactsWithComms = new Set<string>();

        // Process email messages
        for (const message of emailMessages || []) {
          const contact = contacts.find(c => 
            c.work_email === message.from_email || 
            c.personal_email === message.from_email
          );
          if (contact && !contactsWithComms.has(contact.id)) {
            recentCommunications.push({
              id: contact.id,
              first_name: contact.first_name,
              last_name: contact.last_name,
              last_contact: message.date,
              type: 'email' as const
            });
            contactsWithComms.add(contact.id);
          }
        }

        // Process timeline entries
        for (const entry of timelineEntries || []) {
          if (!contactsWithComms.has(entry.contact_id)) {
            const contact = contacts.find(c => c.id === entry.contact_id);
            if (contact) {
              recentCommunications.push({
                id: contact.id,
                first_name: contact.first_name,
                last_name: contact.last_name,
                last_contact: entry.date,
                type: 'manual' as const
              });
              contactsWithComms.add(contact.id);
            }
          }
        }

        // Sort communications by date
        recentCommunications.sort((a, b) => 
          new Date(b.last_contact).getTime() - new Date(a.last_contact).getTime()
        );

        // Filter contacts that need follow-up (no communication in the time range)
        const needsFollowUp = contacts
          .filter(contact => !contactsWithComms.has(contact.id))
          .slice(0, 10);

        setData({
          upcomingBirthdays,
          recentCommunications: recentCommunications.slice(0, 10),
          needsFollowUp
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, [daysRange]);

  return { data, loading, error };
}